import {
  getReportSnapshot,
  summarizeActivity,
  summarizeGanttLoad,
  summarizeIssue,
  suggestReportFilters,
  suggestWorkloadMove,
} from './productContext';
import { STEPS, STEP_META } from './steps';

const SCOPES_EXPLAINER = [
  'GitDone asks GitLab for api + read_user + profile + email so it can load',
  'your groups, projects, issues, and recent activity.',
  'Tokens stay on the GitDone API side after OAuth.',
].join(' ');

const PRICING_EXPLAINER = [
  'You can try GitDone with a trial.',
  'Seats are based on the members you select in the next step —',
  'only choose people you want on the Gantt and contribution views.',
  'Checkout always stays under your control.',
].join(' ');

const welcomeForStep = (step, ctx = {}) => {
  const meta = STEP_META[step] || STEP_META[STEPS.OTHER];
  const report = ctx.report || getReportSnapshot();

  switch (step) {
    case STEPS.TERMS: {
      const greeting = ctx.userName ? `Hi ${ctx.userName}` : 'Hi';
      return [
        `${greeting} — I can walk you through setup.`,
        'First, accept the terms on the left, then sign in with GitLab.',
        'I can accept terms for you, or explain what access GitDone needs.',
      ].join(' ');
    }
    case STEPS.AUTHORIZE:
      return [
        'GitLab is authorizing your account and syncing groups, projects,',
        'and issues. This can take a moment on large groups.',
      ].join(' ');
    case STEPS.GROUP: {
      let tip = '';
      if (ctx.recommendedGroup) {
        const level = ctx.recommendedGroup.level || 'member';
        tip = ` I suggest starting with “${ctx.recommendedGroup.name}” (${level}).`;
      }
      return [
        `Pick the GitLab group you want GitDone to track.${tip}`,
        'Prefer a group where you have at least Reporter access.',
      ].join(' ');
    }
    case STEPS.MEMBERS:
      return [
        'Select the members whose work should appear on Overview and',
        'Progress Report. Fewer seats keep billing focused — you can change',
        'this later in Account.',
      ].join(' ');
    case STEPS.CHECKOUT:
      return [
        'Payment succeeded. Review your group and members, then continue',
        'to Overview — that is your first win in GitDone.',
      ].join(' ');
    case STEPS.DASHBOARD:
      return [
        summarizeActivity({
          activity: ctx.activity,
          members: ctx.members,
          issues: ctx.issues,
        }),
        'I can open Progress Report or explain this Overview anytime.',
      ].join(' ');
    case STEPS.REPORT:
      return [
        summarizeGanttLoad(report),
        'Ask me to suggest filters, summarize workload, or explain a bar.',
      ].join(' ');
    default:
      return [
        `${meta.title}. Ask about connecting GitLab, groups, members,`,
        'or the Progress Report.',
      ].join(' ');
  }
};

export const getQuickActions = (step) => {
  switch (step) {
    case STEPS.TERMS:
      return [
        { id: 'accept_terms', label: 'Accept terms' },
        { id: 'explain_scopes', label: 'GitLab access' },
        { id: 'how_setup_works', label: 'How setup works' },
      ];
    case STEPS.AUTHORIZE:
      return [{ id: 'explain_scopes', label: 'What is syncing?' }];
    case STEPS.GROUP:
      return [
        { id: 'recommend_group', label: 'Recommend a group' },
        { id: 'explain_group', label: 'Why one group?' },
      ];
    case STEPS.MEMBERS:
      return [
        { id: 'recommend_members', label: 'Suggest members' },
        { id: 'explain_pricing', label: 'How do seats work?' },
      ];
    case STEPS.CHECKOUT:
      return [
        { id: 'go_dashboard', label: 'Go to Overview' },
        { id: 'what_next', label: 'What is next?' },
      ];
    case STEPS.DASHBOARD:
      return [
        { id: 'summarize_activity', label: 'Summarize activity' },
        { id: 'go_report', label: 'Open Progress Report' },
        { id: 'explain_overview', label: 'Explain Overview' },
      ];
    case STEPS.REPORT:
      return [
        { id: 'suggest_filters', label: 'Suggest filters' },
        { id: 'summarize_gantt', label: 'Summarize Gantt' },
        { id: 'balance_workload', label: 'Balance workload' },
        { id: 'explain_gantt', label: 'Explain Gantt' },
        { id: 'go_dashboard', label: 'Back to Overview' },
      ];
    default:
      return [{ id: 'how_setup_works', label: 'How does setup work?' }];
  }
};

const recommendGroup = (groups = []) => {
  if (!groups.length) {
    return null;
  }
  const ranked = [...groups].sort((a, b) => {
    const levelA = typeof a.level === 'number' ? a.level : 0;
    const levelB = typeof b.level === 'number' ? b.level : 0;
    return levelB - levelA;
  });
  return ranked[0];
};

const parseGroupQuery = (text, groups = []) => {
  const lower = text.toLowerCase();
  return groups.find((g) => {
    const name = String(g.name || '').toLowerCase();
    return lower.includes(name) || lower.includes(String(g.id));
  });
};

export const createWelcomeMessage = (step, ctx) => ({
  id: `welcome-${step}-${Date.now()}`,
  role: 'agent',
  text: welcomeForStep(step, ctx),
  step,
});

export const runOnboardingTurn = ({ step, text, ctx }) => {
  const input = (text || '').trim();
  const lower = input.toLowerCase();
  const actions = [];
  const replies = [];

  const push = (msg) => replies.push(msg);

  if (!input) {
    return {
      replies: [],
      actions,
    };
  }

  if (
    lower.includes('accept') &&
    (lower.includes('term') || lower.includes('agree'))
  ) {
    actions.push({ type: 'accept_terms' });
    push('Terms marked as accepted. You can sign in with GitLab now.');
  } else if (
    lower.includes('scope') ||
    lower.includes('access') ||
    lower.includes('permission') ||
    lower.includes('sync')
  ) {
    push(SCOPES_EXPLAINER);
  } else if (
    lower.includes('price') ||
    lower.includes('seat') ||
    lower.includes('billing') ||
    lower.includes('trial')
  ) {
    push(PRICING_EXPLAINER);
  } else if (
    lower.includes('how') &&
    (lower.includes('setup') || lower.includes('work') || lower.includes('start'))
  ) {
    push(
      [
        'Setup is: accept terms → GitLab OAuth → pick one group →',
        'choose members (seats) → checkout → Overview.',
        'From Overview, open Progress Report to see the Gantt.',
      ].join(' '),
    );
  } else if (lower.includes('recommend') && step === STEPS.GROUP) {
    const group = recommendGroup(ctx.groups);
    if (group) {
      actions.push({ type: 'select_group', group });
      push(
        `Selected “${group.name}”. Click Continue on the page (or ask me to continue) to move to member selection.`,
      );
    } else {
      push('I do not see groups in your session yet. Finish GitLab authorize, then come back here.');
    }
  } else if (
    (lower.startsWith('select ') || lower.includes('choose ')) &&
    step === STEPS.GROUP
  ) {
    const group = parseGroupQuery(lower, ctx.groups);
    if (group) {
      actions.push({ type: 'select_group', group });
      push(`Selected “${group.name}”. Continue when you are ready.`);
    } else {
      push('Tell me the exact group name from the list, or ask me to recommend one.');
    }
  } else if (
    (lower.includes('continue') || lower.includes('next')) &&
    step === STEPS.GROUP
  ) {
    actions.push({ type: 'continue_group' });
    push('Continuing to member selection…');
  } else if (
    (lower.includes('all member') ||
      lower.includes('everyone') ||
      lower.includes('suggest member') ||
      lower.includes('recommend member')) &&
    step === STEPS.MEMBERS
  ) {
    actions.push({ type: 'select_all_members' });
    push(
      'I selected all members in the group. Review the list, then continue to checkout when ready — I will not start payment for you.',
    );
  } else if (
    (lower.includes('overview') || lower.includes('dashboard')) &&
    (step === STEPS.CHECKOUT ||
      step === STEPS.REPORT ||
      step === STEPS.OTHER ||
      lower.includes('go to') ||
      lower.includes('back'))
  ) {
    actions.push({ type: 'navigate', path: '/dashboard' });
    push('Opening Overview.');
  } else if (
    lower.includes('summarize activity') ||
    (lower.includes('activity') && step === STEPS.DASHBOARD)
  ) {
    push(
      summarizeActivity({
        activity: ctx.activity,
        members: ctx.members,
        issues: ctx.issues,
      }),
    );
  } else if (
    lower.includes('suggest filter') ||
    lower.includes('suggest filters') ||
    (lower.includes('filter') && step === STEPS.REPORT && !lower.includes('label'))
  ) {
    const suggestion = suggestReportFilters(ctx.report || getReportSnapshot());
    suggestion.messages.forEach(push);
    suggestion.actions.forEach((action) => actions.push(action));
  } else if (
    lower.startsWith('filter ') &&
    step === STEPS.REPORT
  ) {
    const labelName = input.slice(7).trim().toLowerCase();
    const labels = (ctx.report || getReportSnapshot()).labels || [];
    const match = labels.find(
      (item) => String(item.label || '').toLowerCase() === labelName,
    );
    if (match) {
      actions.push({ type: 'select_report_label', label: match });
      push(`Filtering the Gantt by label “${match.label}”.`);
    } else {
      push('I could not match that label. Ask me to suggest filters first.');
    }
  } else if (
    lower.includes('summarize gantt') ||
    (lower.includes('workload') && step === STEPS.REPORT)
  ) {
    push(summarizeGanttLoad(ctx.report || getReportSnapshot()));
  } else if (
    lower.includes('ask about issue') ||
    lower.includes('summarize issue') ||
    lower.startsWith('issue ')
  ) {
    push(summarizeIssue(ctx.focusedIssue || {}));
  } else if (
    lower.includes('progress') ||
    lower.includes('gantt') ||
    lower.includes('report')
  ) {
    if (
      step === STEPS.DASHBOARD ||
      step === STEPS.REPORT ||
      step === STEPS.OTHER
    ) {
      if (step !== STEPS.REPORT) {
        actions.push({ type: 'navigate', path: '/data' });
      }
      push(
        [
          'Progress Report maps GitLab issues to rows and uses',
          'created/updated times for timeline bars.',
          'Pick a group and project if the chart is empty.',
        ].join(' '),
      );
    } else {
      push(
        'Finish onboarding first — Progress Report unlocks after activation.',
      );
    }
  } else if (lower.includes('what') && lower.includes('next')) {
    push(welcomeForStep(step, ctx));
  } else if (lower.includes('explain') && step === STEPS.DASHBOARD) {
    push(
      [
        'Overview is your live snapshot: subscribed groups/projects,',
        'issues assigned to tracked members, and recent GitLab activity.',
        'Use the left nav for Progress Report, Projects, Groups, and Account.',
      ].join(' '),
    );
  } else if (lower.includes('explain') && step === STEPS.REPORT) {
    push(
      [
        'Left column = Issues (with assignee).',
        'Timeline bars reflect issue activity windows.',
        'Project header links back to GitLab.',
        'Filters at the top narrow group, project, and labels.',
        'Hover a bar for assignee details, then use Ask guide.',
      ].join(' '),
    );
  } else if (lower.includes('group') && step === STEPS.GROUP) {
    push(
      [
        'GitDone tracks one primary group during onboarding so seats and',
        'billing stay clear. You can adjust subscription groups later from Account.',
      ].join(' '),
    );
  } else if (
    lower.includes('balance workload') ||
    lower.includes('rebalance') ||
    (lower.includes('workload') && !lower.includes('summarize'))
  ) {
    const suggestion = suggestWorkloadMove(
      ctx.report || getReportSnapshot(),
      ctx.members || [],
    );
    push(suggestion.message);
    if (suggestion.proposal) {
      actions.push({
        type: 'store_reassign_proposal',
        proposal: suggestion.proposal,
      });
    }
  } else if (
    lower.includes('confirm reassign') ||
    lower.includes('confirm assign')
  ) {
    if (ctx.reassignProposal) {
      actions.push({
        type: 'confirm_reassign',
        proposal: ctx.reassignProposal,
      });
      push(
        [
          `Writing assignee “${ctx.reassignProposal.assigneeName}”`,
          `for “${ctx.reassignProposal.event.title}” to GitLab…`,
        ].join(' '),
      );
    } else {
      push('No reassign proposal pending. Ask me to balance workload first.');
    }
  } else if (
    lower.includes('move') ||
    lower.includes('reschedule') ||
    lower.includes('due date')
  ) {
    push(
      [
        'Drag the end of a Gantt bar (or move it on the same issue row) to set',
        'the GitLab due date — you will get a confirm dialog before write-back.',
        'Start-date drags stay local. For assignee changes, use Balance workload.',
      ].join(' '),
    );
  } else {
    push(
      [
        `I can help with this step (${STEP_META[step]?.label || 'guide'}).`,
        'Try a quick action below, or ask about activity, filters, or the Gantt.',
      ].join(' '),
    );
  }

  return {
    replies: replies.map((text, i) => ({
      id: `agent-${Date.now()}-${i}`,
      role: 'agent',
      text,
      step,
    })),
    actions,
  };
};

export const runQuickAction = (actionId, step, ctx) => {
  const map = {
    accept_terms: 'accept terms',
    explain_scopes: 'explain gitlab access scopes',
    how_setup_works: 'how does setup work',
    recommend_group: 'recommend a group',
    explain_group: 'why one group',
    recommend_members: 'suggest members',
    explain_pricing: 'how do seats work',
    go_dashboard: 'go to overview',
    go_report: 'open progress report',
    what_next: 'what next',
    explain_overview: 'explain this screen',
    explain_gantt: 'explain the gantt',
    summarize_activity: 'summarize activity',
    suggest_filters: 'suggest filters',
    summarize_gantt: 'summarize gantt',
    balance_workload: 'balance workload',
  };
  return runOnboardingTurn({
    step,
    text: map[actionId] || actionId,
    ctx,
  });
};

export { recommendGroup, SCOPES_EXPLAINER, PRICING_EXPLAINER };
